import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Avatar,
  Chip,
  Tooltip,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  Psychology as ProjectInfoIcon,
  School as KnowledgeHubIcon,
  AutoAwesome as GenerateIcon,
  Add as AddIcon,
  Chat as ChatIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Visibility as ViewProgressIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import useAutomatedGeneration from '../../../hooks/useAutomatedGeneration';
import GenerationProgressDialog from '../../common/GenerationProgressDialog';
import automatedGenerationService from '../../../services/automatedGenerationService';
import PermissionGate from '../../common/PermissionGate';

/**
 * Automated Generation Section Component
 * Displays Project Info and Knowledge Hub tiles with generation capabilities
 */
const AutomatedGenerationSection = ({
  projectId,
  project,
  showNotification,
  onOpenConversation
}) => {
  const [projectInfoStatus, setProjectInfoStatus] = useState('empty'); // 'empty', 'in_progress', 'completed'
  const [knowledgeHubStatus, setKnowledgeHubStatus] = useState('empty');
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [isCheckingFoundation, setIsCheckingFoundation] = useState(false);

  // Use automated generation hook
  const {
    generationStatus,
    isLoading,
    progressLog,
    progressPercentage,
    canStartGeneration,
    isGenerating,
    isPaused,
    isCompleted,
    hasError,
    startGeneration,
    pauseGeneration,
    resumeGeneration,
    resetGeneration,
    restartGeneration
  } = useAutomatedGeneration(projectId, showNotification);

  // Check if foundation content exists
  const checkFoundationContent = useCallback(async () => {
    if (!projectId) return;
    
    setIsCheckingFoundation(true);
    
    try {
      // Call backend API to check foundation content
      const response = await automatedGenerationService.getFoundationContentStatus(projectId);
      const foundationData = response.data;
      
      setProjectInfoStatus(foundationData.has_project_info ? 'completed' : 'empty');
      setKnowledgeHubStatus(foundationData.has_knowledge_hub ? 'completed' : 'empty');
      
      console.log('Foundation content check from backend:', foundationData);
      
    } catch (error) {
      console.error('Error checking foundation content:', error);
      showNotification({
        message: 'Failed to check foundation content status',
        type: 'warning'
      });
    } finally {
      setIsCheckingFoundation(false);
    }
  }, [projectId, showNotification]);

  // Check foundation content when component mounts or projectId changes
  useEffect(() => {
    checkFoundationContent();
  }, [checkFoundationContent]);

  // Handle opening conversations for foundation tiles
  const handleTileClick = useCallback((tileType) => {
    if (onOpenConversation) {
      onOpenConversation(tileType);
      // Refresh foundation content status after user potentially adds content
      setTimeout(() => {
        checkFoundationContent();
      }, 1000); // Give some time for user to add content
    }
  }, [onOpenConversation, checkFoundationContent]);

  // Handle starting automated generation
  const handleStartGeneration = useCallback(async () => {
    try {
      // First, refresh foundation content status to ensure we have the latest data
      setIsCheckingFoundation(true);
      
      // Call backend API to check foundation content
      const response = await automatedGenerationService.getFoundationContentStatus(projectId);
      const foundationData = response.data;
      
      // Update local state
      setProjectInfoStatus(foundationData.has_project_info ? 'completed' : 'empty');
      setKnowledgeHubStatus(foundationData.has_knowledge_hub ? 'completed' : 'empty');
      
      console.log('Foundation content verification before generation:', foundationData);
      
      setIsCheckingFoundation(false);
      
      // Check if we have any foundation content
      if (!foundationData.has_content) {
        showNotification({
          message: 'Please add content to Project Info or Knowledge Hub before generating. Click on the tiles above to add content.',
          type: 'warning'
        });
        return;
      }
      
      // Show which foundation content was found
      const foundContent = [];
      if (foundationData.has_project_info) foundContent.push('Project Info');
      if (foundationData.has_knowledge_hub) foundContent.push('Knowledge Hub');
      
      showNotification({
        message: `Foundation content found: ${foundContent.join(' and ')}. Starting automated generation...`,
        type: 'info'
      });

      setShowProgressDialog(true);
      await startGeneration();
      
    } catch (error) {
      console.error('Error checking foundation content before generation:', error);
      setIsCheckingFoundation(false);
      showNotification({
        message: 'Failed to verify foundation content. Please try again.',
        type: 'error'
      });
    }
  }, [projectId, startGeneration, showNotification]);

  // Handle viewing progress
  const handleViewProgress = useCallback(() => {
    setShowProgressDialog(true);
  }, []);

  // Foundation tiles configuration
  const foundationTiles = [
    {
      id: 'project-info',
      title: 'Project info',
      description: 'Define project information, requirements, and core details',
      icon: ProjectInfoIcon,
      color: '#475969',
      status: projectInfoStatus,
      required: true
    },
    {
      id: 'knowledge-hub',
      title: 'Knowledge hub',
      description: 'Add domain knowledge, references, and supporting documentation',
      icon: KnowledgeHubIcon,
      color: '#475969',
      status: knowledgeHubStatus,
      required: false,
      hidden: true
    }
  ].filter(tile => !tile.hidden);

  // Check if foundation content exists (simplified check)
  const hasFoundationContent = projectInfoStatus !== 'empty' || knowledgeHubStatus !== 'empty';

  // Only show for regular projects, not for new-feature or change-request types
  if (project?.type && project.type !== 'project') {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      {/* Section Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3 
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: '#FF6B35', 
            width: 48, 
            height: 48,
            fontSize: '1.2rem'
          }}>
            <GenerateIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              Automated generation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Set up foundation content and automatically generate all project tasks
            </Typography>
          </Box>
        </Box>
      </Box>
       {/* Generation Control Buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'end' }}>
          {!isGenerating && !isPaused && (
            <>
              <PermissionGate permission="foundation.generate.tasks">
                <Button
                  variant="contained"
                  startIcon={<PlayIcon />}
                  onClick={handleStartGeneration}
                  disabled={!canStartGeneration || isLoading}
                  sx={{
                    bgcolor: '#FF6B35',
                    '&:hover': { bgcolor: '#E5582E' },
                    '&:disabled': { bgcolor: '#ccc' },
                    height: 36,
                    minHeight: 36,
                    textTransform: 'none !important'
                  }}
                >
                  Generate all tasks
                </Button>
              </PermissionGate>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={checkFoundationContent}
                disabled={isCheckingFoundation}
                sx={{ 
                  minWidth: 'auto',
                  height: 36,
                  minHeight: 36
                }}
              >
                Refresh
              </Button>
            </>
          )}

          {isGenerating && (
            <>
              <Button
                variant="outlined"
                startIcon={<PauseIcon />}
                onClick={pauseGeneration}
                color="warning"
                sx={{
                  height: 36,
                  minHeight: 36
                }}
              >
                Pause Generation
              </Button>
              <Button
                variant="outlined"
                startIcon={<ViewProgressIcon />}
                onClick={handleViewProgress}
                color="primary"
                sx={{
                  height: 36,
                  minHeight: 36
                }}
              >
                View Progress
              </Button>
            </>
          )}

          {isPaused && (
            <>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={resumeGeneration}
                color="success"
                sx={{
                  height: 36,
                  minHeight: 36
                }}
              >
                Resume
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={resetGeneration}
                color="primary"
                sx={{
                  height: 36,
                  minHeight: 36
                }}
              >
                Restart
              </Button>
              <Button
                variant="outlined"
                startIcon={<ViewProgressIcon />}
                onClick={handleViewProgress}
                color="primary"
                sx={{
                  height: 36,
                  minHeight: 36
                }}
              >
                View Progress
              </Button>
            </>
          )}

          {(isCompleted || hasError) && (
            <>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={resetGeneration}
                color="primary"
                sx={{
                  height: 36,
                  minHeight: 36
                }}
              >
                Reset
              </Button>
              <Button
                variant="outlined"
                startIcon={<ViewProgressIcon />}
                onClick={handleViewProgress}
                color="primary"
                sx={{
                  height: 36,
                  minHeight: 36
                }}
              >
                View Results
              </Button>
            </>
          )}
        </Box>

      {/* Generation Progress Summary */}
      {(isGenerating || isPaused || isCompleted) && (
        <Box sx={{ mb: 3 }}>
          <Alert 
            severity={
              hasError ? 'error' :
              isCompleted ? 'success' :
              isGenerating ? 'info' : 'warning'
            } 
            sx={{ mb: 2 }}
          >
            <Typography variant="body2">
              {hasError ? 
                `Generation failed: ${generationStatus?.error || 'Unknown error'}` :
              isCompleted ?
                `Generation completed! ${generationStatus?.completed || 0} tasks generated successfully.` :
              isGenerating ? 
                `Generating: ${generationStatus?.currentTask || 'Preparing...'}` :
                `Generation paused at: ${generationStatus?.currentTask || 'Unknown task'}`
              }
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Progress: {generationStatus?.completed || 0} of {generationStatus?.total || 0} tasks
            </Typography>
          </Alert>
          <LinearProgress 
            variant="determinate" 
            value={progressPercentage}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {/* Foundation Tiles */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: foundationTiles.length === 1 ? '1fr' : { xs: '1fr', md: '1fr 1fr' },
        gap: 3,
        mt: 3,
        mb: 4,
        px: 1,
      }}>
        {foundationTiles.map((tile) => {
          const IconComponent = tile.icon;
          const isCompleted = tile.status === 'completed';
          const hasContent = tile.status !== 'empty';
          
          return (
            <Card
              key={tile.id}
              sx={{
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                border: hasContent ? `1px solid ${tile.color}` : '1px solid transparent',
                  boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  borderColor: tile.color
                },
                position: 'relative',
                overflow: 'visible',
                mt: 2,
              }}
              onClick={() => handleTileClick(tile.id)}
            >
              {/* Required Badge */}
              {tile.required && (
                <Chip
                  label="Required"
                  size="small"
                  color="error"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: 12,
                    zIndex: 1
                  }}
                />
              )}

              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Icon */}
                  <Avatar sx={{ 
                    bgcolor: hasContent ? tile.color : '#f5f5f5',
                    color: hasContent ? 'white' : '#666',
                    width: 56, 
                    height: 56 
                  }}>
                    <IconComponent fontSize="large" />
                  </Avatar>

                  {/* Content */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {tile.title}
                      </Typography>
                      {isCompleted && (
                        <Chip label="Ready" size="small" color="success" />
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {tile.description}
                    </Typography>

                    {/* Action buttons */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={`Open ${tile.title} conversation`}>
                        <IconButton 
                          size="small" 
                          sx={{ 
                            bgcolor: hasContent ? `${tile.color}20` : 'action.hover',
                            mr: 2,
                            p: 2,
                            color: hasContent ? tile.color : 'text.secondary',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              bgcolor: hasContent ? `${tile.color}30` : 'action.selected',
                              transform: 'scale(1.1)',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }
                          }}
                        >
                          <ChatIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {!hasContent && (
                        <Tooltip title="Add content">
                          <IconButton 
                            size="small"
                            sx={{ 
                              bgcolor: 'action.hover',
                              transition: 'all 0.3s ease',
                              p: 2,
                              '&:hover': {
                                bgcolor: 'action.selected',
                                transform: 'scale(1.1)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                              }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Help Text */}
      <Alert 
        severity={hasFoundationContent ? "success" : "info"} 
        sx={{ mt: 2 }}
      >
        <Typography variant="body2">
          {hasFoundationContent ? (
            <>
              <strong>Ready to generate!</strong> You have foundation content. 
              Click "Generate all tasks" to automatically create all project documentation with proper dependencies.
            </>
          ) : (
            <>
              <strong>Getting started:</strong> Add your project information and domain knowledge using the tiles above. 
              Then click "Generate all tasks" to automatically create all project documentation.
            </>
          )}
        </Typography>
        {!hasFoundationContent && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
            At least one foundation tile (Project Info or Knowledge Hub) must have content before generation can start.
          </Typography>
        )}
      </Alert>

      {/* Progress Dialog */}
      <GenerationProgressDialog
        open={showProgressDialog}
        onClose={() => setShowProgressDialog(false)}
        generationStatus={generationStatus}
        progressLog={progressLog}
        progressPercentage={progressPercentage}
        onPause={pauseGeneration}
        onResume={resumeGeneration}
        onReset={resetGeneration}
        onRestart={restartGeneration}
        canStartGeneration={canStartGeneration}
        isGenerating={isGenerating}
        isPaused={isPaused}
        isCompleted={isCompleted}
        hasError={hasError}
      />
    </Box>
  );
};

AutomatedGenerationSection.propTypes = {
  projectId: PropTypes.string.isRequired,
  project: PropTypes.object,
  showNotification: PropTypes.func.isRequired,
  onOpenConversation: PropTypes.func
};

export default AutomatedGenerationSection;